import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ProfilePhotoField from "@/components/profile/ProfilePhotoField";

export default function CustomerProfile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ name: "", photo_url: "", city: "", language: "Dansk" });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user) return;
    setForm({
      name: user.name || "",
      photo_url: user.photo_url || "",
      city: user.city || "",
      language: user.language || "Dansk",
    });
  }, [user]);

  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const submit = async (event) => {
    event.preventDefault();
    setMessage("");
    setError("");
    if (!form.name.trim()) {
      setError("Navn er påkrævet.");
      return;
    }
    setBusy(true);
    try {
      await api.put("/users/me/profile", {
        name: form.name.trim(),
        photo_url: form.photo_url || null,
        city: form.city.trim() || null,
        language: form.language.trim() || null,
      });
      await refresh();
      setMessage("Din profil er gemt.");
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Profilen kunne ikke gemmes.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-brand-wash">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-brand-green">
          <ArrowLeft size={15}/> Tilbage
        </Link>
        <div className="mt-4">
          <div className="font-mono uppercase text-[11px] tracking-widest text-brand-muted">Kundeprofil</div>
          <h1 className="font-display text-3xl font-bold text-brand-ink">Min profil</h1>
          <p className="mt-2 text-sm text-brand-muted">
            Din profil er ikke offentlig. Relevante eksperter kan se den, når de hjælper med din forespørgsel.
          </p>
        </div>

        <form onSubmit={submit} className="mt-6 card-outline bg-white p-5 sm:p-7 space-y-5" data-testid="customer-profile-form">
          <ProfilePhotoField
            value={form.photo_url}
            name={form.name}
            onChange={(value) => update("photo_url", value)}
            expert={false}
          />

          <label className="block">
            <span className="text-sm font-medium text-brand-ink">Navn *</span>
            <input value={form.name} onChange={(event) => update("name", event.target.value)} maxLength={120} required className="mt-1 w-full card-outline px-3 py-2.5"/>
          </label>

          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-sm font-medium text-brand-ink">By eller område <span className="font-normal text-brand-muted">(valgfrit)</span></span>
              <input value={form.city} onChange={(event) => update("city", event.target.value)} maxLength={120} placeholder="Fx Aarhus" className="mt-1 w-full card-outline px-3 py-2.5"/>
            </label>
            <label className="block">
              <span className="text-sm font-medium text-brand-ink">Sprog <span className="font-normal text-brand-muted">(valgfrit)</span></span>
              <input value={form.language} onChange={(event) => update("language", event.target.value)} maxLength={60} placeholder="Fx Dansk" className="mt-1 w-full card-outline px-3 py-2.5"/>
            </label>
          </div>

          {error && <div className="text-sm text-[#991B1B]" role="alert">{error}</div>}
          {message && <div className="text-sm text-[#166534]" role="status">{message}</div>}

          <button type="submit" disabled={busy} className="btn-primary inline-flex items-center gap-2 disabled:opacity-50">
            <Save size={15}/> {busy ? "Gemmer…" : "Gem profil"}
          </button>
        </form>
      </div>
    </main>
  );
}

